<!--
  Choosing a card: a compact button that opens a small panel with search, cards grouped by
  issuer (Amex, Chase, …), and other ways to pay (Zelle, cash) at the end.
-->
<script lang="ts">
  import { groupCards } from "@split/core";

  let {
    value = $bindable(""),
    cards,
    label = "Card",
    placeholder = "Not set",
    disabled = false,
    onchange,
  }: {
    value?: string;
    cards: string[];
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    onchange?: (v: string) => void;
  } = $props();

  let open = $state(false);
  let q = $state("");
  let root: HTMLDivElement | undefined = $state();
  let search: HTMLInputElement | undefined = $state();

  const all = $derived(value && !cards.includes(value) ? [...cards, value] : cards);
  const groups = $derived.by(() => {
    const t = q.trim().toLowerCase();
    return groupCards(all)
      .map((g) => ({ ...g, cards: g.cards.filter((c) => !t || c.name.toLowerCase().includes(t) || g.issuer.toLowerCase().includes(t)) }))
      .filter((g) => g.cards.length);
  });

  function pick(v: string) {
    value = v;
    onchange?.(v);
    open = false;
    q = "";
  }

  function toggle() {
    open = !open;
    if (open) queueMicrotask(() => search?.focus());
  }

  $effect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (root && !root.contains(e.target as Node)) open = false;
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && (open = false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  });
</script>

<div class="picker" bind:this={root}>
  <button type="button" class="input trigger" class:empty={!value} {disabled} onclick={toggle} aria-haspopup="listbox" aria-expanded={open} aria-label="{label}: {value || placeholder}">
    <span>{value || placeholder}</span>
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M8 10l4 4 4-4" fill="none" stroke="currentColor" stroke-width="2" /></svg>
  </button>
  {#if open}
    <div class="panel" role="listbox" aria-label={label}>
      {#if all.length > 6}
        <input class="input search" type="search" placeholder="Search cards" bind:value={q} bind:this={search} aria-label="Search cards" />
      {/if}
      <button type="button" role="option" aria-selected={!value} class="opt none" onclick={() => pick("")}>{placeholder}</button>
      {#each groups as g (g.issuer)}
        <div class="grp">
          <p class="issuer">{g.issuer}</p>
          <div class="opts">
            {#each g.cards as c (c.name)}
              <button type="button" role="option" aria-selected={c.name === value} class="opt" class:on={c.name === value} onclick={() => pick(c.name)}>
                {c.name}{#if c.kind === "debit"}<span class="kind">debit</span>{/if}
              </button>
            {/each}
          </div>
        </div>
      {:else}
        <p class="muted small">{q ? "No cards match." : "No cards yet. Add them in the group's settings."}</p>
      {/each}
    </div>
  {/if}
</div>

<style>
  .picker {
    position: relative;
    min-width: 0;
  }

  .trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    width: 100%;
    text-align: left;
    cursor: pointer;
  }

  .trigger span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .trigger.empty {
    color: var(--muted);
  }

  .panel {
    position: absolute;
    z-index: 40;
    top: calc(100% + 4px);
    left: 0;
    width: max(100%, 280px);
    max-height: 340px;
    overflow-y: auto;
    padding: 8px;
    border-radius: var(--r-md);
    background: var(--surface);
    box-shadow: var(--shadow-pop);
    display: grid;
    gap: 6px;
  }

  .search {
    min-height: 34px;
    padding: 4px 10px;
  }

  .issuer {
    margin: 4px 2px 4px;
    font-size: var(--t-xs);
    font-weight: 650;
    color: var(--muted);
  }

  .opts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .opt {
    padding: 5px 10px;
    border: 1px solid var(--rule);
    border-radius: 999px;
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: var(--t-sm);
    cursor: pointer;
  }

  .opt:hover {
    border-color: var(--ink-2);
  }

  .opt.on {
    background: var(--ink);
    border-color: var(--ink);
    color: var(--paper);
  }

  .opt.none {
    justify-self: start;
    border-style: dashed;
    color: var(--muted);
  }

  .kind {
    margin-left: 4px;
    font-size: 0.6875rem;
    opacity: 0.7;
  }

  .small {
    font-size: var(--t-xs);
    margin: 4px;
  }
</style>
